import { MagnifyingGlassIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import toast from 'react-hot-toast';
import { captureException } from '@sentry/react';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ISimpleSelectItem, SimpleSelect } from '../../../components/select/SimpleSelect';
import { Language, SupplierDocumentType } from 'src/types/enums';
import { getSuppliers, getSuppliersForExport, type SupplierExportEntry } from '../models';
import { InternalRole } from '../../auth/endpoints/MeEndpoint.types';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { LinkButton } from '../../../components/button/ButtonLink';
import { Button } from 'components/button/Button';
import { roleToNumber } from 'src/app/auth/roles.client';
import { useAuth } from 'contexts/auth-context';
import { Table } from 'components/table/Table';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from 'components/Breadcrumb';
import { FilterField, FilterPanel, ListPage } from 'components/page-templates/ListPage';
import { useDebouncedSearchParams } from 'src/hooks/useDebouncedSearchParams';
import { sleep } from '@utils/sleep';
import { CheckboxWithLabel } from 'components/checkbox/CheckboxWithLabel';

const TABLE_HEADERS = [
  {
    id: 'accountingId',
    name: 'ID Boekhouding',
  },
  {
    id: 'name',
    name: 'Naam',
  },
  {
    id: 'vatNumber',
    name: 'BTW nummer',
  },
  {
    id: 'address',
    name: 'Adres',
  },
  {
    id: 'postalCode',
    name: 'Postcode',
  },
  {
    id: 'country',
    name: 'Land',
  },
];

const LANGUAGE_FILTER_OPTIONS: ISimpleSelectItem[] = Object.values(Language).map((v) => ({
  key: v,
  name: v,
}));

const SuppliersPage = () => {
  const [searchParams, setSearchParams] = useDebouncedSearchParams();
  const [searchValue, _setSearchValue] = useState(() => searchParams['search'] ?? '');
  const [selectedLanguage, setSelectedLanguage] = useState<ISimpleSelectItem | null>(() => {
    const lang = searchParams['language'];
    return lang ? { key: lang, name: lang } : null;
  });
  const [missingIban, setMissingIban] = useState(() => searchParams['missingIban'] === 'true');
  const [isExporting, setIsExporting] = useState(false);
  const { me } = useAuth();
  const languageFilter = selectedLanguage?.key as Language | undefined;
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getSuppliers(variables.cursor, variables.take, {
        search: searchValue,
        language: languageFilter,
        missingIban,
      });
    },
    [searchValue, languageFilter, missingIban],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 20,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const setSearchValue = useCallback(
    (newValue: string) => {
      setSearchParams({ search: newValue });
      _setSearchValue(newValue);
    },
    [_setSearchValue],
  );

  useEffect(() => {
    page.reset();
  }, [searchValue, languageFilter, missingIban]);

  const isAdmin = roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin);
  const activeFilterCount = [searchValue.trim(), selectedLanguage, missingIban].filter(Boolean).length;
  return (
    <>
      <PageHeader title="Vervoerders" />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={<Breadcrumb currentItem="Vervoerders" />}
            rightSide={
              <>
                {isAdmin && (
                  <Button
                    isLoading={isExporting}
                    isDisabled={isExporting}
                    onTrigger={async () => {
                      if (isExporting) {
                        return;
                      }

                      setIsExporting(true);
                      try {
                        const suppliersDict: Record<string, SupplierExportEntry> = {};

                        let cursor: number | null = null;
                        while (true) {
                          const fetchPage = async () => {
                            return getSuppliersForExport(cursor, 100, {
                              isActive: true,
                              search: searchValue,
                              language: languageFilter,
                            });
                          };

                          let attemptCount = 0;
                          let lastError = null;
                          let suppliers = null;
                          while (attemptCount < 5) {
                            attemptCount++;

                            try {
                              suppliers = await fetchPage();
                              break;
                            } catch (err) {
                              console.error(err);

                              await sleep(5000);
                              lastError = err;
                            }
                          }

                          if (!suppliers) {
                            if (lastError) {
                              throw lastError;
                            }

                            break;
                          }

                          let hasNewData = false;
                          for (const supplier of suppliers) {
                            if (!suppliersDict[supplier.id]) {
                              suppliersDict[supplier.id] = supplier;
                              hasNewData = true;
                            }

                            cursor = supplier.id;
                          }

                          if (!hasNewData) {
                            break;
                          }
                        }

                        const suppliers = Object.values(suppliersDict);
                        const workbook = new ExcelJS.Workbook();
                        workbook.creator = 'TDMS';
                        const worksheet = workbook.addWorksheet('Suppliers');

                        worksheet.columns = [
                          { header: 'Referentie', key: 'ref', width: 10 },
                          { header: 'ID Boekhouding', key: 'accountingId', width: 16 },
                          { header: 'Naam', key: 'name', width: 32 },
                          { header: 'Bedrijfsnummer', key: 'companyNumber', width: 32 },
                          { header: 'BTW Nummer', key: 'vatNumber', width: 32 },
                          { header: 'Straat', key: 'street', width: 16 },
                          { header: 'Huisnummer', key: 'streetNumber', width: 16 },
                          { header: 'Land', key: 'country', width: 16 },
                          { header: 'Postcode', key: 'postalCode', width: 32 },
                          { header: 'Stad', key: 'city', width: 32 },
                          { header: 'Betaaltermijn', key: 'paymentTerm', width: 32 },
                          {
                            header: 'Aansprakelijkheidsverzekering',
                            key: 'civilLiabilityDoc',
                            width: 32,
                          },
                          { header: 'CMR Verzekering', key: 'cmrInsuranceDoc', width: 32 },
                          {
                            header: 'Transport verzekering',
                            key: 'transportInsuranceDoc',
                            width: 32,
                          },
                          { header: 'Vragenlijst', key: 'surveyDoc', width: 32 },
                          { header: 'Factuur e-mail', key: 'invoiceEmail', width: 32 },
                          { header: 'Factuur telefoon', key: 'invoicePhone', width: 32 },
                          { header: 'Offert e-mail', key: 'quotationsEmail', width: 32 },
                          { header: 'Offert telefoon', key: 'quotationsPhone', width: 32 },
                          { header: 'CMR e-mail', key: 'cmrEmail', width: 32 },
                          { header: 'CMR telefoon', key: 'cmrPhone', width: 32 },
                          { header: 'TRO e-mail', key: 'troEmail', width: 32 },
                          { header: 'TRO telefoon', key: 'troPhone', width: 32 },
                        ];

                        for (const supplier of suppliers) {
                          worksheet.addRow({
                            ref: supplier.id,
                            accountingId: supplier.accountingId ?? supplier.id,
                            name: supplier.name,
                            companyNumber: supplier.companyNumber,
                            vatNumber: supplier.vatNumber,
                            street: supplier.street,
                            streetNumber: supplier.streetNumber,
                            country: supplier.country,
                            postalCode: supplier.postalCode,
                            city: supplier.city,
                            paymentTerm: supplier.paymentTerm,
                            civilLiabilityDoc: supplier.documents.find(
                              (v) => v.type === SupplierDocumentType.CivilLiabilityInsurance,
                            )
                              ? 'Yes'
                              : 'No',
                            cmrInsuranceDoc: supplier.documents.find(
                              (v) => v.type === SupplierDocumentType.CmrInsurance,
                            )
                              ? 'Yes'
                              : 'No',
                            transportInsuranceDoc: supplier.documents.find(
                              (v) => v.type === SupplierDocumentType.TransportInsurance,
                            )
                              ? 'Yes'
                              : 'No',
                            surveyDoc: supplier.documents.find((v) => v.type === SupplierDocumentType.Survey)
                              ? 'Yes'
                              : 'No',
                            selfBillingsEmail: supplier.contacts.find((v) => v.shouldReceiveSelfBillings)?.email || '',
                            selfBillingsPhone: supplier.contacts.find((v) => v.shouldReceiveSelfBillings)?.phone || '',
                            troEmail: supplier.contacts.find((v) => v.shouldReceiveTransportOrders)?.email || '',
                            troPhone: supplier.contacts.find((v) => v.shouldReceiveTransportOrders)?.phone || '',
                          });
                        }

                        const xlsxBuffer = await workbook.xlsx.writeBuffer();
                        // eslint-disable-next-line no-restricted-globals
                        const blob = new Blob([xlsxBuffer], {
                          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        });
                        const url = URL.createObjectURL(blob);
                        // eslint-disable-next-line no-restricted-globals
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'vervoerders.xlsx';
                        a.click();
                        URL.revokeObjectURL(url);

                        toast.success('Export voltooid');
                      } catch (err) {
                        captureException(err);
                        console.error(err);
                        toast.error('Vervoerders export mislukt');
                      }
                      setIsExporting(false);
                    }}
                  >
                    Exporteer vervoerders
                  </Button>
                )}
                <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                  Nieuwe vervoerder
                </LinkButton>
              </>
            }
          />
        }
        filters={
          <FilterPanel
            activeCount={activeFilterCount}
            actions={
              <Button
                size={8}
                color="secondary"
                iconLeft={<XIcon className="button-icon" />}
                onTrigger={() => {
                  setSearchValue('');
                  setSelectedLanguage(null);
                  setMissingIban(false);
                  setSearchParams({ language: '', missingIban: '' });
                }}
              >
                Wis alle filters
              </Button>
            }
          >
            <FilterField label="Zoek">
              <Input
                type="text"
                placeholder="Zoek een vervoerder..."
                value={searchValue}
                onChange={setSearchValue}
                iconLeft={<MagnifyingGlassIcon className="input-icon" />}
              />
            </FilterField>
            <FilterField label="Taal">
              <SimpleSelect
                selectedItem={selectedLanguage}
                items={LANGUAGE_FILTER_OPTIONS}
                isOptional={true}
                onSelect={(val) => {
                  setSelectedLanguage(val);
                  setSearchParams({ language: val?.key ?? '' });
                }}
              />
            </FilterField>
            <FilterField label="Bankrekening">
              <CheckboxWithLabel
                labelText="Self billing zonder geldige IBAN"
                isChecked={missingIban}
                onChange={(checked) => {
                  setMissingIban(checked);
                  setSearchParams({ missingIban: checked ? 'true' : '' });
                }}
              />
            </FilterField>
          </FilterPanel>
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={TABLE_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(supplier) => {
            return [
              supplier.accountingId ?? String(supplier.id),
              <Link to={`${supplier.id}/general`} className="link-text">
                {supplier.name}
                {!!supplier.deactivationReason && ` - gedeactiveerd`}
              </Link>,
              supplier.vatNumber,
              `${supplier.street} ${supplier.streetNumber}`,
              supplier.postalCode,
              supplier.country,
            ];
          }}
        />
      </ListPage>
    </>
  );
};

export default SuppliersPage;
